import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { CreateLedgerAccountDialog, isSpendAccount } from "@/components/CreateLedgerAccountDialog";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency } from "@/lib/format";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { Badge } from "@/components/ui/badge";
import { Check, CircleCheck, Plus, Search, SearchX, TriangleAlert } from "lucide-react";
import { EmptyState, HeaderRow, LoadingState, TH, TableShell, prettyCategory } from "@/components/ExpenseUi";
import { InfoHint } from "@/components/InfoHint";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { components } from "@dolfin/api-public";

type MccMappingResponse = components["schemas"]["MccMappingResponse"];
type UnmappedMccResponse = components["schemas"]["UnmappedMccResponse"];
type LedgerAccountResponse = components["schemas"]["LedgerAccountResponse"];

// Needs mapping is ordered by money: mapping the top few covers most spend, and also clears expenses already waiting.

type TabValue = "unmapped" | "all";

// The account pickers' last option opens "Create an account" rather than naming one.
const CREATE_ACCOUNT = "__create_account__";

export function MccMappingsPage() {
  const api = useDolfinClient();
  const [tab, setTab] = useState<TabValue>("unmapped");
  const [mappings, setMappings] = useState<MccMappingResponse[]>([]);
  const [unmapped, setUnmapped] = useState<UnmappedMccResponse[]>([]);
  const [accounts, setAccounts] = useState<LedgerAccountResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // The category whose picker asked to create an account; mapped to it once created.
  const [createForMcc, setCreateForMcc] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [mappingsResult, unmappedResult, accountsResult] = await Promise.all([
      api.GET("/v1/mcc-mappings", {}),
      api.GET("/v1/mcc-mappings/unmapped", {}),
      api.GET("/v1/accounting/ledger-accounts", {}),
    ]);
    setMappings(mappingsResult.data ?? []);
    setUnmapped(unmappedResult.data ?? []);
    setAccounts((accountsResult.data ?? []).filter(isSpendAccount));
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  async function setMapping(mcc: string, ledgerAccountId: string) {
    const { error } = await api.PUT("/v1/mcc-mappings/{mcc}", {
      params: { path: { mcc } },
      body: { ledgerAccountId },
    });
    if (error) {
      toast.error(apiErrorMessage(error, "Couldn't save the mapping."));
    } else {
      toast.success("Mapped. Any spend waiting on this category has been re-coded.");
      await load();
    }
  }

  const query = search.trim().toLowerCase();
  const visibleMappings = query
    ? mappings.filter(
        (m) =>
          m.mcc?.includes(query) ||
          m.label?.toLowerCase().includes(query) ||
          m.ledgerAccountName?.toLowerCase().includes(query),
      )
    : mappings;

  // Bar only when every total is in one currency; summing across currencies would be meaningless.
  const unmappedCurrencies = new Set(unmapped.map((row) => row.currency ?? ""));
  const unmappedTotal = unmapped.reduce((sum, row) => sum + Number(row.totalAmount ?? 0), 0);
  const showShare = unmappedCurrencies.size === 1 && !unmappedCurrencies.has("") && unmappedTotal > 0;

  // `blocking` outlines the picker in amber: only on the Needs mapping tab, where an empty choice is what is
  // holding spend back. On the full list most rows legitimately inherit a default.
  const accountPicker = (mcc: string, current?: string | null, blocking = false) => (
    <Select
      value={current ?? undefined}
      onValueChange={(value) => (value === CREATE_ACCOUNT ? setCreateForMcc(mcc) : setMapping(mcc, value))}
    >
      <SelectTrigger className={cn("h-9 w-64", blocking && "border-amber-500/50")}>
        <SelectValue placeholder="Choose an account" />
      </SelectTrigger>
      <SelectContent>
        {accounts.map((account) => (
          <SelectItem key={account.id} value={account.id!}>
            {account.displayId ? `${account.displayId} · ` : ""}
            {account.name}
          </SelectItem>
        ))}
        <SelectItem value={CREATE_ACCOUNT} className="text-primary">
          <Plus className="size-3.5" /> Create an account…
        </SelectItem>
      </SelectContent>
    </Select>
  );

  const categoryCell = (label: string, mcc: string) => (
    <div className="min-w-0">
      <div className="font-semibold">{prettyCategory(label)}</div>
      <div className="font-mono text-xs text-muted-foreground">MCC {mcc}</div>
    </div>
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Expenses"
        title="Category mapping"
        subtitle="Card networks tag every transaction with a merchant category. Map each one to an account and your spend codes itself."
      />

      {accounts.length === 0 && !loading ? (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <div>
            <p className="text-sm font-semibold">Connect your accounting platform first</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              There are no accounts to map to yet. Connect an accounting platform and sync your chart of
              accounts, then come back.
            </p>
          </div>
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={(v) => setTab(v as TabValue)}>
          <TabsList>
            <TabsTrigger value="unmapped">
              Needs mapping
              {unmapped.length > 0 ? (
                <span className="ml-1.5 rounded-full bg-amber-500/15 px-1.5 text-[10px] font-semibold text-amber-600 tabular-nums">
                  {unmapped.length}
                </span>
              ) : null}
            </TabsTrigger>
            <TabsTrigger value="all">All categories</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* The full list is ~80 categories; finding "hotels" should not mean scrolling for it. */}
        {tab === "all" ? (
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by category, code or account"
              className="h-9 pl-8"
            />
          </div>
        ) : null}
      </div>

      {loading ? (
        <LoadingState label="Loading categories..." />
      ) : tab === "unmapped" ? (
        unmapped.length === 0 ? (
          <EmptyState icon={CircleCheck} title="Everything is mapped">
            Every category your spend has touched codes to an account. New categories appear here the first
            time a card is used in one.
          </EmptyState>
        ) : (
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Category</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Transactions</TableHead>
                  <TableHead className={cn(TH, "text-right")}>Waiting</TableHead>
                  <TableHead className={TH}>Account</TableHead>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {unmapped.map((row) => {
                  const share = showShare ? Number(row.totalAmount ?? 0) / unmappedTotal : 0;
                  return (
                    <TableRow key={row.mcc}>
                      <TableCell>{categoryCell(row.label, row.mcc)}</TableCell>
                      <TableCell className="text-right tabular-nums">{row.transactionCount}</TableCell>
                      <TableCell className="text-right">
                        <div className="font-semibold tabular-nums">
                          {/* In the currency the cards were billed in. A total spanning several currencies
                              has none, and is shown as a bare number rather than mislabelled. */}
                          {row.currency
                            ? formatCurrency(row.totalAmount, row.currency)
                            : Number(row.totalAmount ?? 0).toFixed(2)}
                        </div>
                        {showShare ? (
                          <div className="mt-1 ml-auto flex w-24 items-center gap-1.5">
                            <div className="h-1 flex-1 overflow-hidden rounded-full bg-muted">
                              <div className="h-full rounded-full bg-amber-500" style={{ width: `${share * 100}%` }} />
                            </div>
                            <span className="w-7 text-right text-[10px] text-muted-foreground tabular-nums">
                              {Math.round(share * 100)}%
                            </span>
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell>{accountPicker(row.mcc, undefined, true)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>
        )
      ) : visibleMappings.length === 0 ? (
        <EmptyState icon={SearchX} title="No categories match">
          Nothing matches "{search.trim()}". Try a merchant type, a four-digit code or an account name.
        </EmptyState>
      ) : (
        <TableShell>
          <Table>
            <TableHeader>
              <HeaderRow>
                <TableHead className={TH}>Category</TableHead>
                <TableHead className={TH}>
                  <span className="inline-flex items-center gap-1">
                    Source
                    <InfoHint label="More about the mapping source">
                      Yours means someone in your organisation chose this account. Platform and Dolfin
                      defaults are inherited; picking an account here overrides them.
                    </InfoHint>
                  </span>
                </TableHead>
                <TableHead className={TH}>VAT</TableHead>
                <TableHead className={TH}>Account</TableHead>
              </HeaderRow>
            </TableHeader>
            <TableBody>
              {visibleMappings.map((mapping) => (
                <TableRow key={mapping.mcc}>
                  <TableCell>{categoryCell(mapping.label, mapping.mcc)}</TableCell>
                  <TableCell>
                    {/* Showing the source stops "inherited" looking like "someone here chose this". */}
                    {mapping.source === "OrganisationOverride" ? (
                      <Badge variant="outline" className="border-accent-blue/30 bg-accent-blue/10 text-accent-blue">
                        Yours
                      </Badge>
                    ) : mapping.source === "ClientDefault" ? (
                      <span className="text-xs text-muted-foreground">Platform default</span>
                    ) : mapping.source === "Seed" ? (
                      <span className="text-xs text-muted-foreground">Dolfin default</span>
                    ) : (
                      <Badge
                        variant="outline"
                        className="border-amber-500/30 bg-amber-500/15 text-amber-600"
                      >
                        Unmapped
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {mapping.isVatReclaimable ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <Check className="size-3.5" /> Reclaimable
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not reclaimable</span>
                    )}
                  </TableCell>
                  <TableCell>{accountPicker(mapping.mcc, mapping.ledgerAccountId)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      )}
      <CreateLedgerAccountDialog
        open={createForMcc !== null}
        onOpenChange={(open) => !open && setCreateForMcc(null)}
        purpose="mapping"
        onCreated={(account) => {
          const mcc = createForMcc;
          setCreateForMcc(null);
          if (mcc) setMapping(mcc, account.id!);
        }}
      />
    </PageContainer>
  );
}
